import * as Icons from "@nova/component-icons"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { ContactList, MemberExtended, SavedContact } from "@nova/schemas"
import { formatPhoneNumber, getColorFromId } from "@nova/util-strings"
import classNames from "classnames"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { useSavedContacts } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/SavedContactsQueries"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import React, { useCallback, useMemo, useRef, useState } from "react"
import { MissionGroupIcon } from "features/data-management/workspace/workspace/body/settings-and-collaborators/mission-groups/MissionGroupIcon"

const ChipShell = ({
    children,
    onRemove,
    isWarn,
}: {
    children: React.ReactNode
    onRemove: () => void
    isWarn?: boolean
}) => (
    <div
        className={classNames("flex items-center gap-1 px-2 py-1 rounded-lg shrink-0", {
            "bg-gray-600": !isWarn,
            "bg-yellow-900/40 border border-yellow-600/60": isWarn,
        })}
    >
        {children}
        <Icons.ExtraSmallIcon
            Icon={Icons.XClose}
            className="cursor-pointer text-gray-300 hover:text-gray-0 shrink-0"
            onClick={onRemove}
        />
    </div>
)

const MemberChip = ({ member, onRemove }: { member: MemberExtended; onRemove: () => void }) => (
    <ChipShell onRemove={onRemove}>
        <div className="w-5 h-5 shrink-0 rounded-md overflow-hidden">
            <ProfilePhoto userId={member.userId} fullName={member.fullName ?? ""} />
        </div>
        <span className="text-sm text-gray-0 truncate max-w-[140px]">{member.fullName}</span>
    </ChipShell>
)

const SavedContactEmailChip = ({
    contact,
    onRemove,
}: {
    contact: SavedContact
    onRemove: () => void
}) => (
    <ChipShell onRemove={onRemove}>
        <Icons.SmallIcon Icon={Icons.Email} className="w-5 h-5 shrink-0 text-white" />
        <span className="text-sm text-white truncate max-w-[140px]">{contact.name}</span>
    </ChipShell>
)

const SavedContactPhoneChip = ({
    contact,
    onRemove,
    isWarn,
}: {
    contact: SavedContact
    onRemove: () => void
    isWarn?: boolean
}) => (
    <ChipShell onRemove={onRemove} isWarn={isWarn}>
        <Icons.SmallIcon Icon={Icons.PhoneMessage} className="w-5 h-5 shrink-0 text-white" />
        <span className="text-sm text-white truncate max-w-[140px]">{contact.name}</span>
    </ChipShell>
)

const UnknownEmailChip = ({ email, onRemove }: { email: string; onRemove: () => void }) => (
    <ChipShell onRemove={onRemove}>
        <div
            className="w-5 h-5 shrink-0 rounded flex justify-center items-center"
            style={{ backgroundColor: getColorFromId(email) }}
        >
            <Icons.SmallIcon Icon={Icons.NoAccount} className="shrink-0 text-white-400" />
        </div>
        <span className="text-sm text-white truncate max-w-[140px]">{email}</span>
    </ChipShell>
)

const UnknownPhoneChip = ({
    phone,
    onRemove,
    isWarn,
}: {
    phone: string
    onRemove: () => void
    isWarn?: boolean
}) => (
    <ChipShell onRemove={onRemove} isWarn={isWarn}>
        <Icons.SmallIcon Icon={Icons.PhoneMessage} className="shrink-0 text-white-400" />
        <span className="text-sm text-gray-0 truncate max-w-[140px]">{phone}</span>
    </ChipShell>
)

const MissionGroupChip = ({
    group,
    onRemove,
    isWarn,
}: {
    group: ContactList
    onRemove: () => void
    isWarn?: boolean
}) => {
    return (
        <ChipShell onRemove={onRemove} isWarn={isWarn}>
            <MissionGroupIcon groupId={group.id} size="20" />
            <span className="text-sm text-white truncate max-w-[140px]">{group.name}</span>
        </ChipShell>
    )
}

type ContactChipBoxProps = {
    textInput: string
    setTextInput: (v: string) => void
    emails: string[]
    setEmails: (v: string[]) => void
    phoneNumbers?: string[]
    setPhoneNumbers?: (v: string[]) => void
    missionGroups?: ContactList[]
    setMissionGroups?: (v: ContactList[]) => void
    placeholder?: string
    warnForPhoneNumbers?: boolean
    includePlusIcon?: boolean
    onFocus?: () => void
}

export const ContactChipBox = ({
    textInput,
    setTextInput,
    emails,
    setEmails,
    phoneNumbers = [],
    setPhoneNumbers,
    missionGroups = [],
    setMissionGroups,
    placeholder = "Add emails, comma separated",
    warnForPhoneNumbers,
    includePlusIcon = true,
    onFocus,
}: ContactChipBoxProps) => {
    const members = useAppSelector(selectWorkspaceMembers)
    const { contacts } = useSavedContacts()

    const emailToMember = useMemo(
        () =>
            members?.reduce(
                (acc, cur) => {
                    if (cur.email) acc[cur.email] = cur
                    return acc
                },
                {} as Record<string, MemberExtended>
            ) ?? {},
        [members]
    )

    const { emailToContact, phoneToContact } = useMemo(
        () =>
            contacts.reduce(
                (acc, cur) => {
                    if (cur.email) acc.emailToContact[cur.email] = cur
                    if (cur.phone) acc.phoneToContact[cur.phone] = cur
                    return acc
                },
                { emailToContact: {}, phoneToContact: {} } as {
                    emailToContact: Record<string, SavedContact>
                    phoneToContact: Record<string, SavedContact>
                }
            ),
        [contacts]
    )

    const inputRef = useRef<HTMLInputElement>(null)
    const [inputFocused, setInputFocused] = useState(false)

    const handleInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if ((e.key === "Tab" || e.key === ",") && !!textInput?.length) {
                e.preventDefault()
                const phone = formatPhoneNumber(textInput)
                if (phone) {
                    setPhoneNumbers?.([...phoneNumbers, phone])
                } else {
                    setEmails([...emails, textInput])
                }
                setTextInput("")
            }
        },
        [textInput, emails, phoneNumbers, setEmails, setPhoneNumbers, setTextInput]
    )

    const isAnythingSelected = !!emails?.length || !!phoneNumbers?.length || !!missionGroups?.length
    const isEmpty = !isAnythingSelected && !textInput

    return (
        <div
            className={classNames("rounded-lg border bg-gray-700 cursor-text transition-colors", {
                "border-blue-200": inputFocused,
                "border-transparent": !inputFocused,
            })}
            onClick={() => inputRef.current?.focus()}
        >
            {isAnythingSelected && (
                <ScrollableArea className="max-h-32">
                    <div className="flex flex-wrap gap-1 p-2">
                        {missionGroups.map((group, i) => (
                            <MissionGroupChip
                                key={group.id}
                                group={group}
                                onRemove={() =>
                                    setMissionGroups?.(missionGroups.filter((_, idx) => idx !== i))
                                }
                                isWarn={
                                    warnForPhoneNumbers &&
                                    group.contacts.some((contact) => contact.type === "phone")
                                }
                            />
                        ))}
                        {emails.map((email, i) => {
                            const member = emailToMember[email]
                            if (member) {
                                return (
                                    <MemberChip
                                        key={`email-${email}`}
                                        member={member}
                                        onRemove={() =>
                                            setEmails(emails.filter((_, idx) => idx !== i))
                                        }
                                    />
                                )
                            }
                            const contact = emailToContact[email]
                            if (contact) {
                                return (
                                    <SavedContactEmailChip
                                        key={`email-${email}`}
                                        contact={contact}
                                        onRemove={() =>
                                            setEmails(emails.filter((_, idx) => idx !== i))
                                        }
                                    />
                                )
                            }
                            return (
                                <UnknownEmailChip
                                    key={`email-${email}`}
                                    email={email}
                                    onRemove={() => setEmails(emails.filter((_, idx) => idx !== i))}
                                />
                            )
                        })}
                        {phoneNumbers.map((phone, i) => {
                            const contact = phoneToContact[phone]
                            if (contact) {
                                return (
                                    <SavedContactPhoneChip
                                        key={`phone-${phone}`}
                                        contact={contact}
                                        isWarn={warnForPhoneNumbers}
                                        onRemove={() =>
                                            setPhoneNumbers?.(
                                                phoneNumbers.filter((_, idx) => idx !== i)
                                            )
                                        }
                                    />
                                )
                            }
                            return (
                                <UnknownPhoneChip
                                    key={`phone-${phone}`}
                                    phone={phone}
                                    isWarn={warnForPhoneNumbers}
                                    onRemove={() =>
                                        setPhoneNumbers?.(
                                            phoneNumbers.filter((_, idx) => idx !== i)
                                        )
                                    }
                                />
                            )
                        })}
                    </div>
                </ScrollableArea>
            )}
            <div className="relative flex items-center px-2">
                {isEmpty && includePlusIcon && (
                    <Icons.Icon Icon={Icons.Plus} className="text-gray-300 mr-1" />
                )}
                <input
                    ref={inputRef}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    onFocus={() => {
                        setInputFocused(true)
                        onFocus?.()
                    }}
                    onBlur={() => setInputFocused(false)}
                    placeholder={isEmpty || !inputFocused ? placeholder : ""}
                    className="w-full bg-transparent !border-0 !outline-none !ring-0 text-sm text-gray-0 placeholder-gray-300 min-w-[120px]"
                />
            </div>
        </div>
    )
}
